import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, MapPin, CalendarDays, ThumbsUp } from 'lucide-react';
import { services } from '../data/companyInfo';
import { attachUkAddressAutocomplete } from '../lib/googleMaps';
import { API_BASE } from '../lib/apiBase';
import { trackConfirmedLead } from '../lib/analytics';
import { postBooking, queueBooking } from '../lib/offlineQueue';
import QuoteTrustBadges from './QuoteTrustBadges';

export default function QuoteForm({ locationName = '', hidePromises = false }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupPostcode: '',
    deliveryPostcode: '',
    moveDate: '',
    service: 'house-removals',
    propertySize: '',
    needsPacking: '',
    notes: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [noDate, setNoDate] = useState(false);
  const [apt, setApt] = useState({ bedrooms: '', floor: '', lift: '', liftSize: '' });
  const [pickupFull, setPickupFull] = useState('');
  const [deliveryFull, setDeliveryFull] = useState('');
  const pickupRef = useRef(null);
  const deliveryRef = useRef(null);

  // Lazy-load Google Places Autocomplete on the postcode/address inputs.
  // Progressive enhancement — the form still works if Places fails to load.
  useEffect(() => {
    let cleanups = [];
    // Truncate any value going into a postcode field to a safe length so the
    // backend (max 120 chars) never rejects. Most UK postcodes are <= 10 chars;
    // even with full address fallback we cap at 100 to leave headroom.
    const safePostcode = (v) => String(v || '').trim().slice(0, 100);
    const wire = async () => {
      if (pickupRef.current) {
        cleanups.push(await attachUkAddressAutocomplete(pickupRef.current, (place) => {
          const v = safePostcode(place.postcode || place.formatted);
          setFormData((d) => ({ ...d, pickupPostcode: v }));
          setPickupFull(place.formatted || '');
        }));
      }
      if (deliveryRef.current) {
        cleanups.push(await attachUkAddressAutocomplete(deliveryRef.current, (place) => {
          const v = safePostcode(place.postcode || place.formatted);
          setFormData((d) => ({ ...d, deliveryPostcode: v }));
          setDeliveryFull(place.formatted || '');
        }));
      }
    };
    wire();
    return () => { cleanups.forEach((fn) => { try { fn(); } catch (_) { /* noop */ } }); };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Defensive cap on postcode/address fields so pasted long strings never
    // exceed the backend's 120-char limit and cause a silent failure.
    const v = (name === 'pickupPostcode' || name === 'deliveryPostcode')
      ? String(value).slice(0, 100)
      : value;
    setFormData({ ...formData, [name]: v });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      // Map service slug to human-readable package name for the email
      const serviceMeta = services.find((s) => s.slug === formData.service);
      const packageName = formData.service === 'apartment-removals' ? 'Apartment Removals' : (serviceMeta ? serviceMeta.name : formData.service);

      const aptSelected = formData.propertySize === 'apartment' || formData.service === 'apartment-removals';
      const propertySize = aptSelected
        ? `Apartment — ${apt.bedrooms}, ${apt.floor}, ${apt.lift === 'yes' ? `lift available (${apt.liftSize || 'size not specified'})` : 'no lift'}`
        : formData.propertySize;

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        package: packageName,
        moveDate: noDate ? 'To be confirmed' : (formData.moveDate || 'To be confirmed'),
        pickupPostcode: formData.pickupPostcode,
        pickupAddress: pickupFull || undefined,
        deliveryPostcode: formData.deliveryPostcode,
        deliveryAddress: deliveryFull || undefined,
        propertySize,
        needsPacking: formData.needsPacking,
        notes: locationName
          ? `[Enquiry from ${locationName}] ${formData.notes || ''}`.trim()
          : (formData.notes || '')
      };

      // Timeout-guarded submit (12s) with one automatic retry — on flaky/slow
      // connections the request never hangs forever. If both attempts fail on
      // a NETWORK error, the payload is queued locally and auto-sent the
      // moment the connection returns (see lib/offlineQueue.js).
      let res;
      try {
        res = await postBooking(API_BASE, payload, 12000);
      } catch (_) {
        try {
          res = await postBooking(API_BASE, payload, 12000);
        } catch (_2) {
          queueBooking(payload);
          setStatus('queued');
          return;
        }
      }
      if (!res.ok) {
        setStatus('error');
        return;
      }

      // Try to parse the booking response — if it fails, we still proceed
      // because the booking IS saved server-side (the 2xx is the guarantee).
      let bookingId = '';
      try {
        const booking = await res.json();
        bookingId = (booking && booking.id) || '';
      } catch (err) { console.warn('Booking response parse failed (lead still captured):', err); }

      // Google Ads / GA4: one confirmed_lead per backend-confirmed booking ID (no personal data).
      try {
        trackConfirmedLead({ leadId: bookingId, leadType: 'quote', formSource: 'quote-form', service: formData.service, packageName, propertySize: aptSelected ? apt.bedrooms : formData.propertySize });
      } catch (_) { /* analytics must never break the form */ }
      // First name stays in this tab only — never in the URL (URLs are sent to analytics).
      try {
        sessionStorage.setItem('homego-ty-name', (formData.name || '').split(' ')[0].slice(0, 24));
        sessionStorage.setItem('homego-ty-ref', bookingId.slice(0, 8));
        if (hidePromises) sessionStorage.setItem('homego-ty-origin', 'storage');
        else sessionStorage.removeItem('homego-ty-origin');
      } catch (_) { /* private mode */ }
      const params = new URLSearchParams({ service: formData.service || '' });
      navigate(`/thank-you?${params.toString()}`);
    } catch (err) {
      // Submit failed — log so it surfaces in browser console + Sentry/GA4
      // error tracking, but keep the UX in an "error" state (no crash).
      console.error('QuoteForm submit failed:', err);
      setStatus('error');
    }
  };

  const inputClass =
    'w-full min-h-[42px] px-3 py-2 bg-white border border-gray-300 focus:border-gold-500 focus:ring-2 focus:ring-gold-200 rounded-lg text-gray-900 placeholder-gray-500 outline-none transition-colors text-sm';

  if (status === 'queued') {
    return (
      <div className="bg-white border-2 border-amber-300 rounded-xl p-6 text-center shadow-xl" data-testid="quote-queued">
        <CheckCircle2 className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-1">Request Saved — Will Send Automatically</h3>
        <p className="text-gray-600 text-sm">Your connection dropped, but don&apos;t worry — we&apos;ve saved your quote request on this device and it will send itself the moment you&apos;re back online. Prefer to talk now? Call <a href="tel:+447440414548" className="text-blue-600 font-semibold underline">07440 414548</a> (24/7).</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="bg-white border-2 border-green-400 rounded-xl p-6 text-center shadow-xl" data-testid="quote-success">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-1">Quote Request Received</h3>
        <p className="text-gray-600 text-sm">{hidePromises ? 'We will be in touch about your quote.' : 'We\'ll call you within 2 minutes (we answer 24/7).'} Check your inbox for a confirmation email with your booking reference.</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden bg-gray-950 border border-white/10 rounded-xl p-3.5 sm:p-5 shadow-2xl" data-testid="quote-form-card">
      <div className="relative z-10">
      <QuoteTrustBadges hidePromises={hidePromises} />
      <form onSubmit={handleSubmit} className="space-y-2" data-testid="quote-form" id="quote-form">
      <div className="grid grid-cols-2 gap-2">
        <input name="name" required placeholder="Your Name *" autoComplete="name" aria-label="Your full name" value={formData.name} onChange={handleChange} className={inputClass} data-testid="quote-input-name" />
        <input name="phone" required type="tel" inputMode="tel" autoComplete="tel" placeholder="Phone *" aria-label="Your phone number" value={formData.phone} onChange={handleChange} className={inputClass} data-testid="quote-input-phone" />
      </div>
      <input name="email" required type="email" inputMode="email" autoComplete="email" placeholder="Email Address *" aria-label="Your email address" value={formData.email} onChange={handleChange} className={inputClass} data-testid="quote-input-email" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <input
            ref={pickupRef}
            name="pickupPostcode"
            required
            placeholder={`From Postcode ${locationName && locationName.length <= 20 && !/[:]/.test(locationName) ? `(${locationName})` : '*'}`}
            aria-label="Moving from postcode or address"
            autoComplete="off"
            value={formData.pickupPostcode}
            onChange={(e) => { handleChange(e); setPickupFull(''); }}
            className={inputClass}
            data-testid="quote-input-from"
          />
          {pickupFull && (
            <div className="mt-1 flex items-start gap-1 text-[10px] text-gray-300 leading-snug" data-testid="quote-from-resolved">
              <MapPin className="w-3 h-3 text-gold-400 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{pickupFull}</span>
            </div>
          )}
        </div>
        <div>
          <input
            ref={deliveryRef}
            name="deliveryPostcode"
            required
            placeholder="To Postcode *"
            aria-label="Moving to postcode or address"
            autoComplete="off"
            value={formData.deliveryPostcode}
            onChange={(e) => { handleChange(e); setDeliveryFull(''); }}
            className={inputClass}
            data-testid="quote-input-to"
          />
          {deliveryFull && (
            <div className="mt-1 flex items-start gap-1 text-[10px] text-gray-300 leading-snug" data-testid="quote-to-resolved">
              <MapPin className="w-3 h-3 text-gold-400 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{deliveryFull}</span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="relative min-w-0">
          <CalendarDays className="w-4 h-4 text-gold-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" aria-hidden="true" />
          <input name="moveDate" type="date" aria-label="Preferred move date" value={formData.moveDate} disabled={noDate} onChange={handleChange} className={`${inputClass} quote-date-input pl-9 ${noDate ? 'opacity-40' : ''}`} data-testid="quote-input-date" />
        </div>
        <select name="service" aria-label="Removal service type" value={formData.service} onChange={handleChange} className={`${inputClass} min-w-0`} data-testid="quote-input-service">
          {services.flatMap((s) => s.slug === 'house-removals'
            ? [<option key={s.slug} value={s.slug}>{s.name}</option>, <option key="apartment-removals" value="apartment-removals">Apartment Removals</option>]
            : [<option key={s.slug} value={s.slug}>{s.name}</option>])}
        </select>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-200" data-testid="quote-no-date-toggle">
        <input type="checkbox" checked={noDate} onChange={(e) => { setNoDate(e.target.checked); if (e.target.checked) setFormData((d) => ({ ...d, moveDate: '' })); }} className="w-4 h-4 accent-yellow-500" data-testid="quote-no-date-checkbox" />
        I don&apos;t have a moving date yet
      </label>
      {noDate && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-300 rounded-lg px-3.5 py-3" data-testid="quote-no-date-msg">
          <ThumbsUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-800 leading-snug"><strong className="text-green-700">You can still book!</strong> Secure your quote now and we&apos;ll update your moving date later — free of charge. Booking early protects you from unavailability and more expensive quotes.</p>
        </div>
      )}
      {!noDate && formData.moveDate && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-300 rounded-lg px-3.5 py-3" data-testid="quote-availability-msg">
          <ThumbsUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-800 leading-snug"><strong className="text-green-700">Great!</strong> We have availability {locationName && !/page|landing|blog/i.test(locationName) ? `in ${locationName} ` : ''}on {new Date(`${formData.moveDate}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} — secure it before the slot fills up.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <select
          name="propertySize"
          aria-label="Property size"
          value={formData.propertySize}
          onChange={handleChange}
          className={inputClass}
          data-testid="quote-input-property-size"
          required
        >
          <option value="" disabled>Property Size *</option>
          <option value="apartment">Apartment / Flat</option>
          <option value="studio">Studio Flat</option>
          <option value="1-bed-flat">1 Bed Flat</option>
          <option value="2-bed-house">2 Bed House</option>
          <option value="3-bed-house">3 Bed House</option>
          <option value="4-plus-bed-house">4+ Bed House</option>
          <option value="office">Office / Commercial</option>
          <option value="single-items">Single Items</option>
        </select>
        <select
          name="needsPacking"
          aria-label="Do you need a packing service?"
          value={formData.needsPacking}
          onChange={handleChange}
          className={inputClass}
          data-testid="quote-input-packing"
          required
        >
          <option value="" disabled>Packing? *</option>
          <option value="Yes">Yes — pack for me</option>
          <option value="No">No — I&apos;ll pack myself</option>
          <option value="Not sure">Not sure — advise me</option>
        </select>
      </div>
      {(formData.propertySize === 'apartment' || formData.service === 'apartment-removals') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="quote-apartment-panel">
          <p className="text-amber-800 text-xs font-semibold">Tell us about your apartment</p>
          <select value={apt.bedrooms} onChange={(e) => setApt({ ...apt, bedrooms: e.target.value })} required aria-label="Apartment bedrooms" className={inputClass} data-testid="quote-apt-bedrooms">
            <option value="" disabled>How many bedrooms? *</option>
            <option value="Studio">Studio</option>
            <option value="1 bedroom">1 Bedroom</option>
            <option value="2 bedrooms">2 Bedrooms</option>
            <option value="3 bedrooms">3 Bedrooms</option>
            <option value="4+ bedrooms">4+ Bedrooms</option>
          </select>
          {apt.bedrooms && (
            <select value={apt.floor} onChange={(e) => setApt({ ...apt, floor: e.target.value })} required aria-label="Which floor" className={inputClass} data-testid="quote-apt-floor">
              <option value="" disabled>Which floor? *</option>
              <option value="Ground floor">Ground floor</option>
              <option value="1st floor">1st floor</option>
              <option value="2nd floor">2nd floor</option>
              <option value="3rd floor">3rd floor</option>
              <option value="4th floor">4th floor</option>
              <option value="5th floor or higher">5th floor or higher</option>
            </select>
          )}
          {apt.floor && (
            <select value={apt.lift} onChange={(e) => setApt({ ...apt, lift: e.target.value, liftSize: '' })} required aria-label="Is an elevator available?" className={inputClass} data-testid="quote-apt-lift">
              <option value="" disabled>Is an elevator (lift) available? *</option>
              <option value="yes">Elevator available</option>
              <option value="no">No elevator</option>
            </select>
          )}
          {apt.lift === 'yes' && (
            <select value={apt.liftSize} onChange={(e) => setApt({ ...apt, liftSize: e.target.value })} required aria-label="Elevator size" className={inputClass} data-testid="quote-apt-lift-size">
              <option value="" disabled>Elevator size? *</option>
              <option value="small">Small (2-4 people)</option>
              <option value="medium">Medium (fits boxes &amp; small furniture)</option>
              <option value="large">Large (fits sofas &amp; wardrobes)</option>
            </select>
          )}
          {apt.lift === 'no' && (
            <p className="text-[11px] text-gray-600" data-testid="quote-apt-no-lift-note">No problem — our crew handles stairs every day. We&apos;ll factor it into your quote.</p>
          )}
        </div>
      )}
      {/* Item-list helper — collapsible by default to keep the form compact.
          Click to expand and read a worked example of what to list. */}
      <details className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 group" data-testid="quote-items-helper">
        <summary className="cursor-pointer list-none flex items-center justify-between gap-2 font-semibold text-amber-800">
          <span>📋 Tip: list your items below for an accurate volume-based quote</span>
          <span className="text-[10px] text-gold-500 group-open:rotate-90 transition-transform">▶</span>
        </summary>
        <p className="text-gray-700 mt-1.5 leading-snug">
          Pricing depends on <span className="text-amber-800 font-semibold">total volume</span>, not bedrooms. The more detail, the tighter the price.
        </p>
        <p className="text-gray-600 mt-1 text-[10px] leading-snug">
          <span className="font-semibold text-gray-700">Example:</span> 1× king bed, 1× double bed, 2× wardrobes, 1× chest, 1× 3-seater sofa, 1× dining table + 6 chairs, washing machine, fridge-freezer, 55&quot; TV, ~15 boxes, bike, lawnmower.
        </p>
      </details>
      <textarea
        name="notes"
        placeholder="List your items (beds, sofas, white goods, boxes, garden, bikes…) — plus stairs/lift/parking notes."
        aria-label="List the items you're moving so we can quote accurately based on volume"
        value={formData.notes}
        onChange={handleChange}
        className={inputClass}
        rows="2"
        data-testid="quote-input-notes"
      />

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold py-3.5 rounded-lg text-base transition-colors shadow-lg shadow-gold-500/20 disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="quote-submit-btn"
      >
        {status === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> Securing your quote…</> : 'Get my fixed quote →'}
      </button>

      {status === 'error' && (
        <p className="text-red-600 text-xs text-center" data-testid="quote-error-message">
          Couldn&apos;t send. Please call <a href="tel:+447440414548" className="underline font-semibold">07440 414548</a> directly.
        </p>
      )}
      <p className="text-[11px] text-gray-300 text-center" data-testid="quote-form-footnote">{hidePromises ? 'Takes 30 seconds • No obligation' : 'Takes 30 seconds • No obligation • Quote in 2 minutes'}</p>
    </form>
      </div>
    </div>
  );
}
